import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  ClickAwayListener,
  Badge,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate, useLocation } from 'react-router';
import useWindowSize from 'utilities/hooks/useWindowsSize';
import { checkAppIsRunningInTeams } from 'utilities/authorizationUtils';
import useRefresh from 'utilities/hooks/useRefresh';
import { withTranslation, WithTranslation } from 'react-i18next';
import IStoreState from 'store/IStoreState';
import { connect } from 'react-redux';
import { ReactComponent as LogOutIcon } from 'assets/LogOut.svg';
import { AuthStatus } from 'modules/auth/stateManagement/state';
import NotificationsList from 'shared/modules/appToolbar/NotificationsLists';
import TeamsRefreshIcon from 'shared/icons/TeamsRefreshIcon';
import {
  INotificationsFromStoreState,
  IGetSystemMaintenanceState,
} from 'shared/stateManagement/state';
import { getNotificationsFromStore } from 'shared/stateManagement/thunks';
import { axiosMarkNotificationsFromStoreAsRead } from 'shared/stateManagement/api';
import { getAppTitle } from 'utilities/getAppTitle';
import LogOutDialog from 'modules/auth/components/webLogin/logout/LogOutDialog';
import 'shared/modules/navigationBar/navigationBar.scss';
import HiringManagerRoutes from '@/modules/hiring/components/HiringManagerRoutes';

interface IAppToolbarStateProps {
  authenticated: boolean;
  employeeId: number | undefined;
  notificationsFromStore: INotificationsFromStoreState;
  systemMaintenance: IGetSystemMaintenanceState;
}

interface IAppToolbarDispatchProps {
  getNotificationsFromStore: () => void;
}

const AppToolbar = ({
  t: translate,
  authenticated,
  employeeId,
  getNotificationsFromStore: getNotifications,
  notificationsFromStore,
  systemMaintenance,
}: IAppToolbarStateProps &
  IAppToolbarDispatchProps &
  WithTranslation): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const windowsSize: number[] = useWindowSize();
  const refresh = useRefresh(
    navigate,
    location.pathname + location.search,
    location.state
  );
  const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);
  const [notificationsListOpen, setNotificationsListOpen] =
    useState<boolean>(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [appTitle, setAppTitle] = useState<string>('');

  useEffect(() => {
    setAppTitle(getAppTitle(translate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /* istanbul ignore else */
    if (
      notificationsFromStore.notifications &&
      notificationsFromStore.notifications?.length > 0
    ) {
      const count = notificationsFromStore.notifications?.filter(
        (notification) => notification.isNotificationRead === false
      ).length;
      setNotificationCount(count);
    }
  }, [notificationsFromStore]);

  useEffect(() => {
    /* istanbul ignore else */
    if (employeeId && !checkAppIsRunningInTeams()) getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const onRefreshHandler = async () => {
    /* istanbul ignore else */
    if (employeeId && !checkAppIsRunningInTeams()) getNotifications();
    refresh();
  };

  const resetNotificationsCount = () => {
    /* istanbul ignore else */
    if (notificationCount !== 0) setNotificationCount(0);
  };

  const [notificationsListAnchorEl, setNotificationsListAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleClickOnBellIcon = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setNotificationsListAnchorEl(
      notificationsListAnchorEl !== null ? null : event.currentTarget
    );
    if (notificationsListAnchorEl) {
      setNotificationsListOpen(false);
      resetNotificationsCount();
    } else {
      setNotificationsListOpen(true);
    }

    /* istanbul ignore else */
    if (
      notificationsFromStore.notifications &&
      notificationsFromStore.notifications?.length > 0
    ) {
      const notReadNotifications = notificationsFromStore.notifications.filter(
        (notification) => !notification.isNotificationRead
      );
      /* istanbul ignore else */
      if (notReadNotifications.length > 0) {
        await axiosMarkNotificationsFromStoreAsRead(
          notReadNotifications.map((notification) => notification.id)
        );
      }
    }
  };
  
  const handleClickAwayFromNotificationsList = () => {
    /* istanbul ignore else */
    if (notificationsListOpen) {
      resetNotificationsCount();
      setNotificationsListOpen(false);
      setNotificationsListAnchorEl(null);
    }
  };

  /* istanbul ignore else */
  if (!authenticated && !systemMaintenance.isFetchingData) return <></>;

  return (
    <>
      <Grid
        container
        className='nav-bar'
        justifyContent='space-between'
        flexWrap='nowrap'
        data-testid='nav-bar'
      >
        <Grid item sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Grid
            container
            flexWrap='nowrap'
            sx={{ justifyContent: 'flex-start' }}
          >
            <Box display='flex' alignItems='center' tabIndex={0}>
              <img
                className='nav-bar__logo'
                src={window.location.origin + '/hm-logo.svg'}
                alt='HM Logo'
              />
              <Typography
                variant='h2'
                sx={{
                  fontSize: '1.125rem!important',
                  fontWeight: '700!important',
                  whiteSpace: 'nowrap',
                  width: { xs: '110px', sm: 'auto' },
                  overflow: 'hidden',
                  textOverflow: { xs: 'ellipsis', sm: 'initial' },
                }}
              >
                {appTitle}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid item>
          <Box className='navbar-action-buttons'>
            <Grid
              container
              justifyContent='center'
              alignItems='center'
              flexWrap='nowrap'
            >
              {windowsSize[0] < 375 ? undefined : (
                <Grid
                  item
                  onKeyPress={async (event: any) => {
                    /* istanbul ignore else */
                    if (event.charCode === 13) onRefreshHandler();
                  }}
                  aria-labelledby='navbar-refresh-button'
                  tabIndex={0}
                >
                  <IconButton
                    id='navbar-refresh-button'
                    aria-label={translate('refreshButtonLabel')}
                    data-testid='navbar-refresh-button'
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    onClick={async () => onRefreshHandler()}
                    tabIndex={-1}
                    role='presentation'
                  >
                    <TeamsRefreshIcon />
                  </IconButton>
                </Grid>
              )}
              {!checkAppIsRunningInTeams() && (
                <>
                  {windowsSize[0] < 375 ? undefined : (
                    <Grid
                      item
                      onKeyPress={async (event: any) => {
                        /* istanbul ignore else */
                        if (event.charCode === 13) setLogoutDialogOpen(true);
                      }}
                      aria-labelledby='navbar-logout-button'
                      tabIndex={0}
                    >
                      <IconButton
                        id='navbar-logout-button'
                        data-testid='navbar-logout-button'
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() => setLogoutDialogOpen(true)}
                        tabIndex={-1}
                        aria-label={translate('logoutButton')}
                        role='presentation'
                      >
                        <LogOutIcon />
                      </IconButton>
                    </Grid>
                  )}
                  <Grid
                    item
                    onKeyPress={async (event: any) => {
                      /* istanbul ignore else */
                      if (event.charCode === 13) handleClickOnBellIcon(event);
                    }}
                    aria-labelledby='navbar-notifications-button'
                    tabIndex={0}
                  >
                    <ClickAwayListener
                      onClickAway={handleClickAwayFromNotificationsList}
                    >
                      <Box>
                        <IconButton
                          id='navbar-notifications-button'
                          data-testid='navbar-notifications-button'
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          onClick={(e) => {
                            handleClickOnBellIcon(e);
                          }}
                          tabIndex={-1}
                          aria-label={`${translate('notificationButton', {
                            notificationCount: notificationCount,
                          })}`}
                          role='presentation'
                        >
                          <Badge
                            badgeContent={notificationCount}
                            color='primary'
                            data-testid='notification-count-icon'
                            id='notification-count-icon'
                          >
                            <NotificationsIcon />
                          </Badge>
                        </IconButton>

                        {notificationsListOpen ? (
                          <NotificationsList
                            anchorEl={notificationsListAnchorEl}
                            open={notificationsListOpen}
                            notifications={notificationsFromStore.notifications}
                            notificationsAreAllRead={notificationCount === 0}
                            loading={
                              notificationsFromStore.isFetchingData ||
                              (!notificationsFromStore.error &&
                                notificationsFromStore.notifications === null)
                            }
                          />
                        ) : null}
                      </Box>
                    </ClickAwayListener>
                  </Grid>
                </>
              )}
              <LogOutDialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
              />
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Box id='appPageContentContainer'>
      <HiringManagerRoutes />
      </Box>
    </> 
  );
};

const mapStateToProps = (state: IStoreState) => ({
  authenticated: state.auth.status === AuthStatus.authenticated,
  employeeId: state.employee.employee?.id,
  notificationsFromStore: state.notificationsFromStore,
  systemMaintenance: state.systemMaintenance,
});

const mapDispatchToProps = {
  getNotificationsFromStore,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AppToolbar));
